import { type HostStateCode } from '@schemas'
import { HostStateSelect } from '@/components/host-state-select'
import { catalogChoices, catalogChoiceKey, type CatalogChoice } from '@/lib/catalog-choices'
import { SeriesField } from '@/components/shows/series-field'
import {
  cloneElement,
  isValidElement,
  useCallback,
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Stepper, useStepperContext, type StepperContextValue } from '@/components/stepper/Stepper'
import { PageHeading } from '@/components/page-heading'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'

import {
  useOrganizations,
  useCreateOrganization,
  useCreateShow,
  useCatalogClasses,
  useAddCatalogClassToShow,
  useAddShowStaff,
  usePeopleSearch,
} from '@/api/hooks'
import { useCurrentShow } from '@/context/current-show'
import { normalizeDateInputValue, toApiDateOnly } from '@/lib/date'
import { parseApiErrorMessage } from '@/lib/api-errors'
import type {
  AddCatalogClassToShowInput,
  CreateShowRoleAssignmentInput,
  TOrganization,
  TPerson,
  TShow,
} from '@schemas'

type StaffRole = Extract<
  CreateShowRoleAssignmentInput['role'],
  'organizer' | 'lead_secretary' | 'secretary' | 'steward' | 'judge'
>

type BasicsState = {
  name: string
  startDate: string
  endDate: string
  location: string
  hostState: HostStateCode | null
  rulesVersion: string
}

type OrganizationState = {
  mode: 'select' | 'create'
  orgId: string
  lockedOrgId?: string
  newOrg: {
    name: string
    contact: string
    abbreviation: string
    website: string
  }
}

type OptionsState = {
  enableClubs: boolean
  labelOnlyMode: boolean
  labelPrintingDisabled: boolean
  reserveGrandEnabled: boolean
  staffMayExhibit: boolean
}

type ClassIntent = {
  id: string
  catalogClassId: string
  catalogSubtypeId?: string
  catalogClassName: string
  catalogCategoryCode?: string
  catalogCategoryName?: string
  divisionId?: string
  divisionCode?: string
  divisionName?: string
  divisionOrder?: number
  code?: string
  name?: string
}

type StaffAssignmentDraft = {
  id: string
  role: StaffRole
  personId?: string
  name?: string
  contact?: string
}

type WizardState = {
  basics: BasicsState
  organization: OrganizationState
  options: OptionsState
  classes: ClassIntent[]
  staff: StaffAssignmentDraft[]
}

const STAFF_ROLE_CONFIG: Record<
  StaffRole,
  { title: string; helper: string; minimum?: number; recommended?: boolean }
> = {
  organizer: {
    title: 'Organizers',
    helper: 'Organizers can update show details and finalize the show. Assign at least one.',
    minimum: 1,
  },
  secretary: {
    title: 'Secretaries',
    helper: 'Secretaries record results and manage intake. Assign at least one secretary or lead.',
    minimum: 1,
  },
  lead_secretary: {
    title: 'Lead Secretaries',
    helper: 'Lead Secretaries can update show status, finalize awards, and manage staff.',
  },
  steward: {
    title: 'Stewards',
    helper: 'Stewards assist judges and manage logistics.',
  },
  judge: {
    title: 'Judges',
    helper: 'Assign at least one judge who will evaluate entries.',
    recommended: true,
  },
}

const VISIBLE_STAFF_ROLES: StaffRole[] = [
  'organizer',
  'lead_secretary',
  'secretary',
  'steward',
  'judge',
]

const OPTION_CONFIG: Array<{ key: keyof OptionsState; label: string; helper: string }> = [
  {
    key: 'enableClubs',
    label: 'Enable clubs',
    helper: 'Allow entrants to associate club memberships with this show.',
  },
  {
    key: 'labelOnlyMode',
    label: 'Label-only mode',
    helper: 'Restrict the show to label printing only (no run or judging tools).',
  },
  {
    key: 'labelPrintingDisabled',
    label: 'Disable label printing',
    helper: 'Create Exhibit ID labels manually for this show.',
  },
  {
    key: 'staffMayExhibit',
    label: 'Show staff may exhibit',
    helper:
      'Allow show staff to enter their own exhibits. Staff roles control entry-management access separately.',
  },
  {
    key: 'reserveGrandEnabled',
    label: 'Reserve Grand Champion',
    helper: 'Track Reserve Grand Champion alongside Grand Champion awards.',
  },
]

type BasicsTouchedState = Record<keyof BasicsState, boolean>

const EMPTY_BASICS_TOUCHED: BasicsTouchedState = {
  name: false,
  startDate: false,
  endDate: false,
  location: false,
  hostState: false,
  rulesVersion: false,
}

function todayDate() {
  return normalizeDateInputValue(new Date().toISOString())
}

function initialWizardState(lockedOrgId?: string): WizardState {
  const today = todayDate()
  return {
    basics: {
      name: '',
      startDate: today,
      endDate: today,
      location: '',
      hostState: null,
      rulesVersion: '',
    },
    organization: {
      mode: lockedOrgId ? 'select' : 'select',
      orgId: lockedOrgId ?? '',
      lockedOrgId,
      newOrg: {
        name: '',
        contact: '',
        abbreviation: '',
        website: '',
      },
    },
    options: {
      enableClubs: true,
      labelOnlyMode: false,
      labelPrintingDisabled: false,
      reserveGrandEnabled: false,
      staffMayExhibit: true,
    },
    classes: [],
    staff: [],
  }
}

type FailedStaffAssignment = StaffAssignmentDraft & { error: string }
type ClassSeedFailure = { id: string; name: string; message: string }

export default function NewShowWizard() {
  const [searchParams] = useSearchParams()
  const lockedOrgId = searchParams.get('orgId') ?? undefined
  const [state, setState] = useState<WizardState>(() => initialWizardState(lockedOrgId))
  const [seriesId, setSeriesId] = useState<string | null | undefined>(null)
  const [seriesValid, setSeriesValid] = useState(true)
  const [catalogSearch, setCatalogSearch] = useState('')
  const [failedStaff, setFailedStaff] = useState<FailedStaffAssignment[]>([])
  const [classFailures, setClassFailures] = useState<ClassSeedFailure[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submissionError, setSubmissionError] = useState<string | null>(null)
  const [createdShow, setCreatedShow] = useState<TShow | null>(null)
  const [postSubmitNotice, setPostSubmitNotice] = useState<string | null>(null)
  const hasNavigatedToSetup = useRef(false)
  const [basicsTouched, setBasicsTouched] = useState<BasicsTouchedState>(() => ({
    ...EMPTY_BASICS_TOUCHED,
  }))

  const navigate = useNavigate()
  const { setCurrentShow } = useCurrentShow()

  const organizationsQuery = useOrganizations()
  const createOrganization = useCreateOrganization()
  const createShow = useCreateShow()
  const addCatalogClassToShow = useAddCatalogClassToShow()
  const addShowStaff = useAddShowStaff()

  const organizations = useMemo(
    () => (organizationsQuery.data ?? []).filter(org => Boolean(org.id)),
    [organizationsQuery.data]
  )

  useEffect(() => {
    if (!lockedOrgId) return
    setState(prev => {
      if (prev.organization.orgId === lockedOrgId) return prev
      return {
        ...prev,
        organization: {
          ...prev.organization,
          orgId: lockedOrgId,
          lockedOrgId,
        },
      }
    })
  }, [lockedOrgId])

  const catalogQueryEnabled = catalogSearch.trim().length >= 2
  const catalogClassesQuery = useCatalogClasses(
    catalogQueryEnabled ? { q: catalogSearch.trim() } : undefined
  )
  const [defaultCatalogClasses, setDefaultCatalogClasses] = useState<CatalogChoice[]>(() => {
    return catalogChoices(catalogClassesQuery.data ?? [])
  })

  useEffect(() => {
    if (!catalogQueryEnabled && catalogClassesQuery.data) {
      setDefaultCatalogClasses(catalogChoices(catalogClassesQuery.data))
    }
  }, [catalogClassesQuery.data, catalogQueryEnabled])

  const catalogClasses = useMemo<CatalogChoice[]>(() => {
    if (catalogQueryEnabled) {
      return catalogChoices(catalogClassesQuery.data ?? [])
    }
    return defaultCatalogClasses
  }, [catalogClassesQuery.data, catalogQueryEnabled, defaultCatalogClasses])

  const updateBasics = (patch: Partial<BasicsState>) => {
    setState(prev => ({ ...prev, basics: { ...prev.basics, ...patch } }))
  }
  const markBasicsFieldTouched = (field: keyof BasicsState) => {
    setBasicsTouched(prev => (prev[field] ? prev : { ...prev, [field]: true }))
  }
  const markAllBasicsTouched = () => {
    setBasicsTouched({
      name: true,
      startDate: true,
      endDate: true,
      location: true,
      hostState: true,
      rulesVersion: true,
    })
  }

  const updateOrganization = (patch: Partial<OrganizationState>) => {
    setState(prev => ({ ...prev, organization: { ...prev.organization, ...patch } }))
  }

  const updateOptions = (patch: Partial<OptionsState>) => {
    setState(prev => ({ ...prev, options: { ...prev.options, ...patch } }))
  }

  const updateClasses = (next: ClassIntent[] | ((current: ClassIntent[]) => ClassIntent[])) => {
    setState(prev => ({
      ...prev,
      classes:
        typeof next === 'function'
          ? (next as (c: ClassIntent[]) => ClassIntent[])(prev.classes)
          : next,
    }))
  }

  const updateStaff = (next: StaffAssignmentDraft[]) => {
    setState(prev => ({ ...prev, staff: next }))
  }

  const basicsErrors = computeBasicsErrors(state.basics)
  const basicsValid = Object.values(basicsErrors).every(message => message === null)
  const organizationValid = Boolean(state.organization.orgId)
  const staffValidation = computeStaffValidation(state.staff)
  const staffValid = staffValidation.hasOrganizer && staffValidation.hasSecretaryOrLead

  const autoDivisionPlans = useMemo(() => computeAutoDivisionPlans(state.classes), [state.classes])

  const selectedOrg = useMemo(() => {
    return organizations.find(org => org.id === state.organization.orgId)
  }, [organizations, state.organization.orgId])

  const orgOptions = useMemo(() => {
    return organizations
      .slice()
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
      .map(org => ({
        id: org.id as string,
        name: org.name ?? 'Unnamed organization',
      }))
  }, [organizations])

  const catalogSelectedIds = useMemo(
    () =>
      state.classes
        .filter(
          item =>
            !item.divisionId &&
            (!item.divisionCode || item.divisionCode === `DIV-${item.catalogCategoryCode}`)
        )
        .map(item => catalogChoiceKey(item.catalogClassId, item.catalogSubtypeId)),
    [state.classes]
  )
  const handleAddClass = (catalogClass: CatalogChoice) => {
    if (!catalogClass.id) return
    updateClasses(prev => {
      if (
        prev.some(
          item =>
            catalogChoiceKey(item.catalogClassId, item.catalogSubtypeId) === catalogClass.id &&
            !item.divisionId &&
            (!item.divisionCode || item.divisionCode === `DIV-${item.catalogCategoryCode}`)
        )
      ) {
        return prev
      }
      return [
        ...prev,
        {
          id: crypto.randomUUID?.() ?? `class-${Date.now()}`,
          catalogClassId: catalogClass.modelId,
          catalogSubtypeId: catalogClass.catalogSubtypeId,
          catalogClassName: catalogClass.name ?? 'Unnamed class',
          catalogCategoryCode: catalogClass.categoryCode,
          catalogCategoryName: catalogClass.categoryName,
        },
      ]
    })
  }

  const handleRemoveClass = (id: string) => {
    updateClasses(prev => prev.filter(item => item.id !== id))
  }

  const handleClassFieldChange = (id: string, patch: Partial<ClassIntent>) => {
    updateClasses(prev => prev.map(item => (item.id === id ? { ...item, ...patch } : item)))
  }

  const handleAddStaff = (
    role: StaffRole,
    assignment: Omit<StaffAssignmentDraft, 'id' | 'role'>
  ) => {
    const normalizedAssignment = {
      ...assignment,
      name: assignment.name?.trim() || assignment.name || undefined,
      contact: assignment.contact?.trim() || assignment.contact || undefined,
    }
    updateStaff([
      ...state.staff,
      {
        id: crypto.randomUUID?.() ?? `staff-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        role,
        ...normalizedAssignment,
      },
    ])
  }

  const handleRemoveStaff = (id: string) => {
    updateStaff(state.staff.filter(member => member.id !== id))
  }

  const handleCreateOrganization = async () => {
    const payload = {
      name: state.organization.newOrg.name.trim(),
      contact: state.organization.newOrg.contact.trim() || undefined,
      abbreviation: state.organization.newOrg.abbreviation.trim() || undefined,
      website: state.organization.newOrg.website.trim() || undefined,
    }
    if (!payload.name) {
      toast.error('Organization name is required')
      return false
    }
    try {
      const organization = await createOrganization.mutateAsync(payload)
      if (!organization.id) {
        throw new Error('Organization identifier missing from response')
      }
      toast.success(`Created organization “${organization.name ?? 'Organization'}”`)
      await organizationsQuery.refetch()
      updateOrganization({
        orgId: organization.id,
        mode: 'select',
        newOrg: { name: '', contact: '', abbreviation: '', website: '' },
      })
      return true
    } catch (error) {
      const message = parseApiErrorMessage(error)
      toast.error(message)
      return false
    }
  }

  const handleSubmit = async () => {
    if (!basicsValid || !organizationValid || !staffValid) {
      if (!basicsValid) {
        markAllBasicsTouched()
      }
      setSubmissionError('Resolve validation items above before creating the show.')
      return
    }
    const startDate = toApiDateOnly(state.basics.startDate)
    const endDate = toApiDateOnly(state.basics.endDate)
    if (!startDate || !endDate) {
      setSubmissionError('Enter valid show dates.')
      return
    }
    if (!seriesValid) {
      setSubmissionError('Choose an available series or None before creating the show.')
      return
    }
    if (!state.organization.orgId) {
      setSubmissionError('Select an organization for this show.')
      return
    }

    setSubmissionError(null)
    setPostSubmitNotice(null)
    setIsSubmitting(true)
    setFailedStaff([])
    setClassFailures([])
    try {
      const result = await createShow.mutateAsync({
        orgId: state.organization.orgId,
        ...(seriesId ? { seriesId } : {}),
        name: state.basics.name.trim(),
        startDate,
        endDate,
        location: state.basics.location.trim() || undefined,
        hostState: state.basics.hostState,
        rulesVersion: state.basics.rulesVersion.trim() || undefined,
        enableClubs: state.options.enableClubs,
        labelOnlyMode: state.options.labelOnlyMode,
        labelPrintingDisabled: state.options.labelPrintingDisabled,
        reserveGrandEnabled: state.options.reserveGrandEnabled,
        staffMayExhibit: state.options.staffMayExhibit,
      })

      if (!result.id) {
        throw new Error('Show identifier missing from response')
      }

      setCreatedShow(result)
      setPostSubmitNotice('Show created. Configure classes or continue to Setup when ready.')
      const classErrors = await seedSelectedClasses(
        result.id,
        state.classes,
        addCatalogClassToShow,
        autoDivisionPlans
      )
      if (classErrors.length > 0) {
        setClassFailures(classErrors)
        const message = 'Show created, but some classes could not be added. Review the list below.'
        toast.warning(message)
        setPostSubmitNotice(message)
      }
      const staffErrors = await assignInitialStaff(result.id, state.staff, addShowStaff)

      if (staffErrors.length > 0) {
        setFailedStaff(staffErrors)
        const message =
          'Show created, but some staff assignments failed. Review the errors below or add them later from Setup.'
        setPostSubmitNotice(message)
        toast.warning(message)
        return
      }

      toast.success(`Created show “${result.name ?? 'Show'}”`)
      setPostSubmitNotice('Show created successfully. Continue to Setup to keep working.')
    } catch (error) {
      const message = parseApiErrorMessage(error)
      setSubmissionError(message)
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRetryFailedStaff = async () => {
    if (failedStaff.length === 0 || !createdShow?.id) return
    setIsSubmitting(true)
    setSubmissionError(null)
    try {
      const retryErrors = await assignInitialStaff(createdShow.id, failedStaff, addShowStaff)
      if (retryErrors.length > 0) {
        setFailedStaff(retryErrors)
        setSubmissionError('Some staff assignments are still failing. Try again from Setup.')
      } else {
        setFailedStaff([])
        toast.success('All pending staff assignments were added.')
      }
    } catch (error) {
      const message = parseApiErrorMessage(error)
      setSubmissionError(message)
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const navigateToSetup = useCallback(() => {
    if (!createdShow) return
    setCurrentShow(createdShow)

    const slugDestination =
      createdShow.slug && createdShow.slug.length > 0 ? `/${createdShow.slug}/setup` : '/setup'
    navigate(slugDestination, { replace: true })
  }, [createdShow, navigate, setCurrentShow])

  useEffect(() => {
    if (!createdShow) return
    if (failedStaff.length > 0) return
    if (isSubmitting) return
    if (hasNavigatedToSetup.current) return
    hasNavigatedToSetup.current = true
    navigateToSetup()
  }, [createdShow, failedStaff, isSubmitting, navigateToSetup])

  const steps = [
    {
      id: 'basics',
      label: 'Basics',
      content: (
        <BasicsStep
          basics={state.basics}
          errors={basicsErrors}
          touched={basicsTouched}
          onChange={updateBasics}
          onBlurField={markBasicsFieldTouched}
        />
      ),
      renderFooter: (context: StepperContextValue) => (
        <StepFooter
          disableBack
          onNext={() => {
            if (!basicsValid) {
              markAllBasicsTouched()
              return
            }
            context.goToNext()
          }}
          nextLabel="Next"
        />
      ),
    },
    {
      id: 'organization',
      label: 'Organization',
      content: (
        <div className="space-y-6">
          <OrganizationStep
            organizationState={state.organization}
            organizations={orgOptions}
            lockedOrgId={lockedOrgId}
            onModeChange={mode => updateOrganization({ mode })}
            onSelectOrg={orgId => updateOrganization({ orgId })}
            onNewOrgChange={patch =>
              updateOrganization({ newOrg: { ...state.organization.newOrg, ...patch } })
            }
            onCreateOrganization={handleCreateOrganization}
            isCreating={createOrganization.isPending}
            organizationsLoading={organizationsQuery.isLoading}
          />
          <SeriesField
            organizationId={state.organization.orgId}
            value={seriesId}
            onChange={setSeriesId}
            disabled={isSubmitting}
            onValidityChange={setSeriesValid}
          />
        </div>
      ),
      renderFooter: (context: StepperContextValue) => (
        <StepFooter
          onBack={context.goToPrevious}
          onNext={context.goToNext}
          nextDisabled={!organizationValid || !seriesValid}
        />
      ),
    },
    {
      id: 'options',
      label: 'Options',
      content: <OptionsStep options={state.options} onChange={updateOptions} />,
      renderFooter: (context: StepperContextValue) => (
        <StepFooter onBack={context.goToPrevious} onNext={context.goToNext} />
      ),
    },
    {
      id: 'classes',
      label: 'Classes',
      description: 'Optional',
      content: (
        <ClassesStep
          classes={state.classes}
          catalogClasses={catalogClasses}
          catalogLoading={catalogClassesQuery.isLoading}
          searchValue={catalogSearch}
          onSearchChange={setCatalogSearch}
          onAddClass={handleAddClass}
          onRemoveClass={handleRemoveClass}
          onClassChange={handleClassFieldChange}
          stagedCatalogIds={catalogSelectedIds}
          autoPlans={autoDivisionPlans}
        />
      ),
      renderFooter: (context: StepperContextValue) => (
        <StepFooter
          onBack={context.goToPrevious}
          onNext={context.goToNext}
          extraAction={
            <Button type="button" variant="ghost" onClick={() => context.goToStep('staff')}>
              Skip for now
            </Button>
          }
        />
      ),
    },
    {
      id: 'staff',
      label: 'Staff',
      content: (
        <StaffStep
          staff={state.staff}
          onAddStaff={handleAddStaff}
          onRemoveStaff={handleRemoveStaff}
          validation={staffValidation}
        />
      ),
      renderFooter: (context: StepperContextValue) => (
        <StepFooter
          onBack={context.goToPrevious}
          onNext={context.goToNext}
          nextDisabled={!staffValid}
        />
      ),
    },
    {
      id: 'review',
      label: 'Review',
      content: (
        <>
          <ReviewStep
            state={state}
            selectedOrg={selectedOrg ?? null}
            staffValidation={staffValidation}
            submissionError={submissionError}
            failedStaff={failedStaff}
            autoPlans={autoDivisionPlans}
            classFailures={classFailures}
            postSubmitNotice={postSubmitNotice}
            createdShow={createdShow}
          />
          <div className="mt-4">
            <SeriesField
              organizationId={state.organization.orgId}
              value={seriesId}
              onChange={setSeriesId}
              disabled
              onValidityChange={setSeriesValid}
            />
            <p className="mt-2 text-sm">Edition year: {state.basics.startDate.slice(0, 4)}</p>
          </div>
        </>
      ),
      renderFooter: (context: StepperContextValue) => (
        <StepFooter
          onBack={context.goToPrevious}
          nextLabel={createdShow ? 'Go to setup' : isSubmitting ? 'Creating…' : 'Create show'}
          nextDisabled={isSubmitting}
          onNext={createdShow ? navigateToSetup : handleSubmit}
          extraAction={
            failedStaff.length > 0 ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleRetryFailedStaff}
                disabled={isSubmitting}
              >
                Retry failed staff
              </Button>
            ) : null
          }
        />
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeading>Create a new show</PageHeading>
      <p className="text-muted-foreground">
        Follow the wizard to capture show details, options, classes, and staff assignments.
      </p>
      <Stepper
        steps={steps}
        initialStepId="basics"
        ariaLabel="New Show steps"
        testIdPrefix="new-show-step"
      />
    </div>
  )
}

function computeBasicsErrors(basics: BasicsState) {
  const errors: Record<keyof BasicsState | 'range', string | null> = {
    name: basics.name.trim() ? null : 'Show name is required',
    startDate: basics.startDate ? null : 'Start date is required',
    endDate: basics.endDate ? null : 'End date is required',
    location: null,
    hostState: null,
    rulesVersion: null,
    range: null,
  }
  const startDate = basics.startDate ? new Date(basics.startDate) : null
  const endDate = basics.endDate ? new Date(basics.endDate) : null
  if (startDate && endDate && startDate.getTime() > endDate.getTime()) {
    errors.range = 'Start date must be before or equal to end date.'
  }
  return errors
}

function computeStaffValidation(staff: StaffAssignmentDraft[]) {
  const counts = VISIBLE_STAFF_ROLES.reduce<Record<StaffRole, number>>(
    (acc, role) => {
      acc[role] = staff.filter(person => person.role === role).length
      return acc
    },
    {
      organizer: 0,
      lead_secretary: 0,
      secretary: 0,
      steward: 0,
      judge: 0,
    }
  )
  const hasOrganizer = counts.organizer > 0
  const hasSecretaryOrLead = counts.secretary > 0 || counts.lead_secretary > 0
  return { counts, hasOrganizer, hasSecretaryOrLead }
}

type AutoDivisionPlan = {
  divisionCode: string
  divisionName: string
  divisionOrder: number
}

function computeAutoDivisionPlans(classes: ClassIntent[]): Map<string, AutoDivisionPlan> {
  const planByCategory = new Map<string, AutoDivisionPlan>()
  const planBySelection = new Map<string, AutoDivisionPlan>()
  const usedOrders = new Set<number>()
  let nextOrder = 1

  for (const selection of classes) {
    const categoryCode = (selection.catalogCategoryCode ?? 'GEN').toUpperCase()
    if (!planByCategory.has(categoryCode)) {
      let order = nextOrder
      while (usedOrders.has(order)) {
        order += 1
      }
      nextOrder = order + 1
      usedOrders.add(order)
      planByCategory.set(categoryCode, {
        divisionCode: `DIV-${categoryCode}`,
        divisionName: selection.catalogCategoryName ?? `Division ${categoryCode}`,
        divisionOrder: order,
      })
    }
    planBySelection.set(selection.id, planByCategory.get(categoryCode)!)
  }

  return planBySelection
}

async function seedSelectedClasses(
  showId: string,
  selections: ClassIntent[],
  mutation: ReturnType<typeof useAddCatalogClassToShow>,
  autoPlans: Map<string, AutoDivisionPlan>
): Promise<ClassSeedFailure[]> {
  const failures: ClassSeedFailure[] = []
  for (const selection of selections) {
    try {
      const payload: AddCatalogClassToShowInput = {
        catalogClassId: selection.catalogClassId,
        catalogSubtypeId: selection.catalogSubtypeId,
        divisionId: selection.divisionId,
        divisionCode: selection.divisionCode?.trim() || undefined,
        divisionName: selection.divisionName?.trim() || undefined,
        divisionOrder:
          typeof selection.divisionOrder === 'number' ? selection.divisionOrder : undefined,
        code: selection.code?.trim() || undefined,
        name: selection.name?.trim() || undefined,
      }
      const hasDivision =
        payload.divisionId ||
        payload.divisionCode ||
        payload.divisionName ||
        typeof payload.divisionOrder === 'number'
      if (!hasDivision) {
        const defaultPlan = autoPlans.get(selection.id)
        if (defaultPlan) {
          payload.divisionCode = defaultPlan.divisionCode
          payload.divisionName = defaultPlan.divisionName
          payload.divisionOrder = defaultPlan.divisionOrder
        }
      }
      await mutation.mutateAsync({ showId, payload })
    } catch (error) {
      failures.push({
        id: selection.id,
        name: selection.catalogClassName,
        message: parseApiErrorMessage(error),
      })
    }
  }
  return failures
}

async function assignInitialStaff(
  showId: string,
  staff: Array<StaffAssignmentDraft | FailedStaffAssignment>,
  mutation: ReturnType<typeof useAddShowStaff>
) {
  const errors: FailedStaffAssignment[] = []
  for (const assignment of staff) {
    try {
      const payload: CreateShowRoleAssignmentInput = {
        role: assignment.role,
      }
      if (assignment.personId) {
        payload.personId = assignment.personId
      } else {
        payload.name = assignment.name?.trim() || undefined
        payload.contact = assignment.contact?.trim() || undefined
      }
      await mutation.mutateAsync({ showId, payload })
    } catch (error) {
      errors.push({
        ...assignment,
        error: parseApiErrorMessage(error),
      })
    }
  }
  return errors
}

type BasicsStepProps = {
  basics: BasicsState
  errors: Record<keyof BasicsState | 'range', string | null>
  touched: BasicsTouchedState
  onChange: (patch: Partial<BasicsState>) => void
  onBlurField: (field: keyof BasicsState) => void
}

function BasicsStep({ basics, errors, touched, onChange, onBlurField }: BasicsStepProps) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Show name" required error={errors.name} showError={touched.name}>
          <Input
            value={basics.name}
            onChange={event => onChange({ name: event.target.value })}
            onBlur={() => onBlurField('name')}
            placeholder="e.g., Honey Harvest Classic"
          />
        </Field>
        <Field label="Location" error={errors.location} showError={touched.location}>
          <Input
            value={basics.location}
            onChange={event => onChange({ location: event.target.value })}
            onBlur={() => onBlurField('location')}
            placeholder="Venue or city"
          />
        </Field>
        <Field label="Host state" error={errors.hostState} showError={touched.hostState}>
          <HostStateSelect
            value={basics.hostState}
            onChange={hostState => onChange({ hostState })}
          />
        </Field>
        <Field label="Start date" required error={errors.startDate} showError={touched.startDate}>
          <Input
            type="date"
            value={basics.startDate}
            onChange={event => onChange({ startDate: event.target.value })}
            onBlur={() => onBlurField('startDate')}
          />
        </Field>
        <Field label="End date" required error={errors.endDate} showError={touched.endDate}>
          <Input
            type="date"
            min={basics.startDate}
            value={basics.endDate}
            onChange={event => onChange({ endDate: event.target.value })}
            onBlur={() => onBlurField('endDate')}
          />
        </Field>
        <Field label="Rules version" error={errors.rulesVersion} showError={touched.rulesVersion}>
          <Input
            value={basics.rulesVersion}
            onChange={event => onChange({ rulesVersion: event.target.value })}
            onBlur={() => onBlurField('rulesVersion')}
            placeholder="Optional reference"
          />
        </Field>
      </div>
      {errors.range && touched.startDate && touched.endDate && (
        <p className="text-sm text-destructive">{errors.range}</p>
      )}
    </div>
  )
}

type FieldProps = {
  label: string
  required?: boolean
  error?: string | null
  showError?: boolean
  children: ReactNode
}

function Field({ label, required, error, showError = true, children }: FieldProps) {
  const id = useId()
  const control = isValidElement(children)
    ? cloneElement(children as React.ReactElement<any>, {
        id,
      })
    : children

  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {control}
      {error && showError && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

type OrganizationStepProps = {
  organizationState: OrganizationState
  organizations: Array<{ id: string; name: string }>
  lockedOrgId?: string
  onModeChange: (mode: OrganizationState['mode']) => void
  onSelectOrg: (orgId: string) => void
  onNewOrgChange: (patch: Partial<OrganizationState['newOrg']>) => void
  onCreateOrganization: () => Promise<boolean>
  isCreating: boolean
  organizationsLoading: boolean
}

function OrganizationStep({
  organizationState,
  organizations,
  lockedOrgId,
  onModeChange,
  onSelectOrg,
  onNewOrgChange,
  onCreateOrganization,
  isCreating,
  organizationsLoading,
}: OrganizationStepProps) {
  const stepper = useStepperContext()
  const [localCreatePending, setLocalCreatePending] = useState(false)
  const hasOrganizations = organizations.length > 0
  const rawMode = hasOrganizations ? organizationState.mode : 'create'
  const activeMode = lockedOrgId ? 'select' : rawMode

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Host organization</h3>
          <p className="text-sm text-muted-foreground">
            Select an organization or create a new one inline.
          </p>
        </div>
      </div>

      {hasOrganizations ? (
        <Tabs
          value={activeMode}
          onValueChange={value => {
            if (lockedOrgId) return
            onModeChange(value as OrganizationState['mode'])
          }}
          className="space-y-4"
        >
          <TabsList>
            <TabsTrigger value="select">Select existing</TabsTrigger>
            <TabsTrigger value="create" disabled={Boolean(lockedOrgId)}>
              Create new
            </TabsTrigger>
          </TabsList>
          <TabsContent value="select" className="mt-0 space-y-4">
            {renderSelectCard()}
          </TabsContent>
          <TabsContent value="create" className="mt-0 space-y-4">
            {renderCreateCard()}
          </TabsContent>
        </Tabs>
      ) : (
        renderCreateCard()
      )}
    </div>
  )

  function renderSelectCard() {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Choose organization</CardTitle>
          <CardDescription>
            {lockedOrgId
              ? 'Organization is preselected from the Organizations page.'
              : 'Pick the organization that owns this show.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select
            value={organizationState.orgId || undefined}
            onValueChange={onSelectOrg}
            disabled={lockedOrgId !== undefined || organizationsLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select organization" />
            </SelectTrigger>
            <SelectContent>
              {organizations.map(org => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {lockedOrgId && (
            <p className="text-sm text-muted-foreground">
              To create a show for a different organization, start from the landing page.
            </p>
          )}
        </CardContent>
      </Card>
    )
  }

  function renderCreateCard() {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Create organization</CardTitle>
          <CardDescription>We will save it before continuing.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Organization name" required>
              <Input
                value={organizationState.newOrg.name}
                onChange={event => onNewOrgChange({ name: event.target.value })}
                placeholder="e.g., Honey Show Association"
              />
            </Field>
            <Field label="Contact">
              <Input
                value={organizationState.newOrg.contact}
                onChange={event => onNewOrgChange({ contact: event.target.value })}
                placeholder="Name, email, or phone"
              />
            </Field>
            <Field label="Abbreviation">
              <Input
                value={organizationState.newOrg.abbreviation}
                onChange={event => onNewOrgChange({ abbreviation: event.target.value })}
                placeholder="Optional short code"
              />
            </Field>
            <Field label="Website">
              <Input
                value={organizationState.newOrg.website}
                onChange={event => onNewOrgChange({ website: event.target.value })}
                placeholder="https://example.org"
              />
            </Field>
          </div>
          <Button
            type="button"
            onClick={async () => {
              setLocalCreatePending(true)
              const success = await onCreateOrganization()
              setLocalCreatePending(false)
              if (success) {
                stepper.goToNext()
              }
            }}
            disabled={isCreating || localCreatePending}
          >
            {isCreating || localCreatePending ? 'Saving…' : 'Save & select'}
          </Button>
        </CardContent>
      </Card>
    )
  }
}

type OptionsStepProps = {
  options: OptionsState
  onChange: (patch: Partial<OptionsState>) => void
}

function OptionsStep({ options, onChange }: OptionsStepProps) {
  return (
    <div className="space-y-4">
      {OPTION_CONFIG.map(toggle => (
        <div
          key={toggle.key}
          className="flex items-start justify-between gap-4 rounded-lg border p-4"
        >
          <div>
            <p className="font-medium">{toggle.label}</p>
            <p className="text-sm text-muted-foreground">{toggle.helper}</p>
          </div>
          <input
            type="checkbox"
            className="h-4 w-4 rounded border border-input"
            checked={options[toggle.key]}
            onChange={event => onChange({ [toggle.key]: event.target.checked })}
            aria-label={toggle.label}
          />
        </div>
      ))}
    </div>
  )
}

type ClassesStepProps = {
  classes: ClassIntent[]
  catalogClasses: CatalogChoice[]
  catalogLoading: boolean
  searchValue: string
  onSearchChange: (value: string) => void
  onAddClass: (catalogClass: CatalogChoice) => void
  onRemoveClass: (id: string) => void
  onClassChange: (id: string, patch: Partial<ClassIntent>) => void
  stagedCatalogIds: string[]
  autoPlans: Map<string, AutoDivisionPlan>
}

function ClassesStep({
  classes,
  catalogClasses,
  catalogLoading,
  searchValue,
  onSearchChange,
  onAddClass,
  onRemoveClass,
  onClassChange,
  stagedCatalogIds,
  autoPlans,
}: ClassesStepProps) {
  const [selectedClasses, setSelectedClasses] = useState<Map<string, CatalogChoice>>(
    () => new Map()
  )
  const stagedSet = useMemo(() => new Set(stagedCatalogIds), [stagedCatalogIds])

  useEffect(() => {
    setSelectedClasses(prev => {
      let changed = false
      const next = new Map<string, CatalogChoice>()
      prev.forEach((catalogClass, id) => {
        if (stagedSet.has(id)) {
          changed = true
          return
        }
        next.set(id, catalogClass)
      })
      return changed ? next : prev
    })
  }, [stagedSet])

  const toggleSelection = (catalogClass: CatalogChoice) => {
    const catalogClassId = catalogClass.id
    if (!catalogClassId || stagedSet.has(catalogClassId)) return
    setSelectedClasses(prev => {
      const next = new Map(prev)
      if (next.has(catalogClassId)) {
        next.delete(catalogClassId)
      } else {
        next.set(catalogClassId, catalogClass)
      }
      return next
    })
  }

  const handleSelectVisible = () => {
    setSelectedClasses(prev => {
      const next = new Map(prev)
      catalogClasses.forEach(catalogClass => {
        if (catalogClass.id && !stagedSet.has(catalogClass.id)) {
          next.set(catalogClass.id, catalogClass)
        }
      })
      return next
    })
  }

  const handleClearSelection = () => {
    setSelectedClasses(() => new Map())
  }

  const handleAddSelected = () => {
    if (selectedClasses.size === 0) return
    selectedClasses.forEach(catalogClass => {
      onAddClass(catalogClass)
    })
    setSelectedClasses(() => new Map())
  }

  const selectedCount = selectedClasses.size
  const selectableVisibleClasses = catalogClasses.filter(
    catalogClass => catalogClass.id && !stagedSet.has(catalogClass.id)
  )
  const allVisibleSelected =
    selectableVisibleClasses.length > 0 &&
    selectableVisibleClasses.every(
      catalogClass => catalogClass.id && selectedClasses.has(catalogClass.id)
    )

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Catalog classes</CardTitle>
          <CardDescription>
            Search the catalog and add classes to seed into the show.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            placeholder="Search catalog classes"
            value={searchValue}
            onChange={event => onSearchChange(event.target.value)}
          />
          <div className="flex flex-wrap gap-2 text-sm">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleSelectVisible}
              disabled={selectableVisibleClasses.length === 0 || allVisibleSelected}
            >
              {allVisibleSelected ? 'All visible selected' : 'Select visible'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClearSelection}
              disabled={selectedCount === 0}
            >
              Clear selection
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleAddSelected}
              disabled={selectedCount === 0}
            >
              Add selected{selectedCount > 0 ? ` (${selectedCount})` : ''}
            </Button>
          </div>
          {catalogLoading ? (
            <p className="text-sm text-muted-foreground">Loading catalog…</p>
          ) : catalogClasses.length === 0 ? (
            <p className="text-sm text-muted-foreground">No catalog classes match your search.</p>
          ) : (
            <div className="grid gap-3">
              {catalogClasses.map(catalogClass => (
                <div
                  key={catalogClass.id}
                  className="flex items-center justify-between rounded-md border p-3"
                >
                  <div>
                    <p className="font-medium">{catalogClass.name}</p>
                    <p className="text-xs text-muted-foreground">{catalogClass.categoryCode}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${catalogClass.name}`}
                      disabled={!catalogClass.id || stagedSet.has(catalogClass.id)}
                      checked={Boolean(catalogClass.id && selectedClasses.has(catalogClass.id))}
                      onChange={() => toggleSelection(catalogClass)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onAddClass(catalogClass)}
                      disabled={!catalogClass.id}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {selectedCount > 0 && (
            <div className="rounded-md border border-dashed border-muted p-3 text-sm text-muted-foreground">
              <p className="font-semibold text-foreground">Selected classes</p>
              <ul className="mt-2 list-disc space-y-1 pl-4">
                {Array.from(selectedClasses.values()).map(catalogClass => (
                  <li key={catalogClass.id}>{catalogClass.name}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Selected classes</CardTitle>
          <CardDescription>
            Configure division targets or rename classes before they are seeded. Leave blank to use
            catalog defaults.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {classes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No classes selected yet.</p>
          ) : (
            classes.map(selection => {
              const autoPlan = autoPlans.get(selection.id)
              const usesAutoPlan =
                !selection.divisionId &&
                !selection.divisionCode &&
                !selection.divisionName &&
                !selection.divisionOrder
              return (
                <div key={selection.id} className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-medium">{selection.catalogClassName}</p>
                      {selection.catalogCategoryCode && (
                        <p className="text-xs text-muted-foreground">
                          Catalog category: {selection.catalogCategoryCode}
                        </p>
                      )}
                      {usesAutoPlan && autoPlan && (
                        <p className="text-xs text-muted-foreground">
                          Default division: {autoPlan.divisionName} ({autoPlan.divisionCode})
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => onRemoveClass(selection.id)}
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <Input
                      value={selection.code ?? ''}
                      onChange={event => onClassChange(selection.id, { code: event.target.value })}
                      placeholder="Class code (optional)"
                    />
                    <Input
                      value={selection.name ?? ''}
                      onChange={event => onClassChange(selection.id, { name: event.target.value })}
                      placeholder="Class name override"
                    />
                    <Input
                      value={selection.divisionCode ?? ''}
                      onChange={event =>
                        onClassChange(selection.id, { divisionCode: event.target.value })
                      }
                      placeholder="Division code (optional)"
                    />
                    <Input
                      value={selection.divisionName ?? ''}
                      onChange={event =>
                        onClassChange(selection.id, { divisionName: event.target.value })
                      }
                      placeholder="Division name"
                    />
                    <Input
                      type="number"
                      value={selection.divisionOrder?.toString() ?? ''}
                      onChange={event => {
                        const raw = event.target.value.trim()
                        const parsed = Number(raw)
                        onClassChange(selection.id, {
                          divisionOrder:
                            raw.length === 0 || Number.isNaN(parsed) ? undefined : parsed,
                        })
                      }}
                      placeholder="Division order (optional)"
                    />
                  </div>
                </div>
              )
            })
          )}
        </CardContent>
      </Card>
    </div>
  )
}

type StaffStepProps = {
  staff: StaffAssignmentDraft[]
  onAddStaff: (role: StaffRole, assignment: Omit<StaffAssignmentDraft, 'id' | 'role'>) => void
  onRemoveStaff: (id: string) => void
  validation: ReturnType<typeof computeStaffValidation>
}

function StaffStep({ staff, onAddStaff, onRemoveStaff, validation }: StaffStepProps) {
  return (
    <div className="space-y-4">
      {!validation.hasOrganizer && (
        <Alert message="Assign at least one Organizer before continuing." />
      )}
      {!validation.hasSecretaryOrLead && (
        <Alert message="Assign a Secretary or Lead Secretary before continuing." />
      )}
      <div className="grid gap-4">
        {VISIBLE_STAFF_ROLES.map(role => (
          <StaffRoleCard
            key={role}
            role={role}
            assignments={staff.filter(member => member.role === role)}
            onAdd={assignment => onAddStaff(role, assignment)}
            onRemove={onRemoveStaff}
            minimum={STAFF_ROLE_CONFIG[role].minimum}
            helper={STAFF_ROLE_CONFIG[role].helper}
          />
        ))}
      </div>
    </div>
  )
}

type AlertProps = { message: string }

function Alert({ message }: AlertProps) {
  return (
    <div className="rounded-md border border-destructive bg-destructive/5 p-3 text-sm text-destructive">
      {message}
    </div>
  )
}

type StaffRoleCardProps = {
  role: StaffRole
  assignments: StaffAssignmentDraft[]
  onAdd: (assignment: Omit<StaffAssignmentDraft, 'id' | 'role'>) => void
  onRemove: (id: string) => void
  minimum?: number
  helper: string
}

function StaffRoleCard({ role, assignments, onAdd, onRemove, helper }: StaffRoleCardProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [manualName, setManualName] = useState('')
  const [manualContact, setManualContact] = useState('')
  const deferredTerm = useDeferredValue(searchTerm)
  const searchEnabled = deferredTerm.trim().length >= 2
  const peopleQuery = usePeopleSearch(
    { q: searchEnabled ? deferredTerm.trim() : undefined },
    { enabled: searchEnabled }
  )

  const filteredResults = useMemo(() => {
    if (!peopleQuery.data?.people) return []
    const existingIds = new Set(assignments.map(assignment => assignment.personId).filter(Boolean))
    return peopleQuery.data.people.filter(person => !existingIds.has(person.id ?? ''))
  }, [assignments, peopleQuery.data])

  const handleManualAdd = () => {
    if (!manualName.trim()) {
      toast.error('Enter a name to add staff')
      return
    }
    onAdd({ name: manualName.trim(), contact: manualContact.trim() || undefined })
    setManualName('')
    setManualContact('')
  }

  const handlePersonSelect = (person: TPerson) => {
    if (!person.id) return
    onAdd({ personId: person.id, name: person.name ?? undefined })
    setSearchTerm('')
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{STAFF_ROLE_CONFIG[role].title}</CardTitle>
        <CardDescription>{helper}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Search people</Label>
          <Input
            value={searchTerm}
            onChange={event => setSearchTerm(event.target.value)}
            placeholder="Type at least two characters"
          />
          {searchTerm.length > 0 && !searchEnabled && (
            <p className="text-xs text-muted-foreground">Type at least two characters to search.</p>
          )}
          {searchEnabled && (
            <div className="rounded-md border">
              {peopleQuery.isFetching ? (
                <p className="p-3 text-sm text-muted-foreground">Searching…</p>
              ) : filteredResults.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">No matching people.</p>
              ) : (
                <ul className="divide-y">
                  {filteredResults.map(person => (
                    <li
                      key={person.id ?? person.name}
                      className="flex items-center justify-between gap-4 p-3 text-sm"
                    >
                      <div>
                        <p className="font-medium">{person.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {[person.email, person.phone].filter(Boolean).join(' • ')}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePersonSelect(person)}
                      >
                        Add
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <Separator />

        <div className="space-y-2">
          <Label>Add custom contact</Label>
          <div className="grid gap-2 md:grid-cols-2">
            <Input
              value={manualName}
              onChange={event => setManualName(event.target.value)}
              placeholder="Full name"
            />
            <Input
              value={manualContact}
              onChange={event => setManualContact(event.target.value)}
              placeholder="Email or phone"
            />
          </div>
          <Button type="button" variant="outline" onClick={handleManualAdd}>
            Add custom staff
          </Button>
        </div>

        <Separator />

        <div className="space-y-2">
          <Label>Assigned {STAFF_ROLE_CONFIG[role].title.toLowerCase()}</Label>
          {assignments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No staff assigned yet.</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {assignments.map(assignment => (
                <li key={assignment.id} className="flex items-center justify-between gap-4 p-3">
                  <div>
                    <p className="font-medium">
                      {assignment.name?.trim() || 'Selected staff member'}
                    </p>
                    {assignment.contact && (
                      <p className="text-sm text-muted-foreground">{assignment.contact}</p>
                    )}
                  </div>
                  <Button type="button" variant="ghost" onClick={() => onRemove(assignment.id)}>
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

type ReviewStepProps = {
  state: WizardState
  selectedOrg: TOrganization | null
  staffValidation: ReturnType<typeof computeStaffValidation>
  submissionError: string | null
  failedStaff: FailedStaffAssignment[]
  autoPlans: Map<string, AutoDivisionPlan>
  classFailures: ClassSeedFailure[]
  postSubmitNotice: string | null
  createdShow: TShow | null
}

function ReviewStep({
  state,
  selectedOrg,
  staffValidation,
  submissionError,
  failedStaff,
  autoPlans,
  classFailures,
  postSubmitNotice,
  createdShow,
}: ReviewStepProps) {
  return (
    <div className="space-y-4">
      {createdShow && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
          {postSubmitNotice ?? `Show “${createdShow.name ?? 'Show'}” created successfully.`}
        </div>
      )}
      {submissionError && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
          {submissionError}
        </div>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Basics</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-2 text-sm">
          <div>
            <p className="text-muted-foreground">Name</p>
            <p className="font-medium">{state.basics.name || 'Not set'}</p>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            <div>
              <p className="text-muted-foreground">Start</p>
              <p className="font-medium">{state.basics.startDate || 'Not set'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">End</p>
              <p className="font-medium">{state.basics.endDate || 'Not set'}</p>
            </div>
          </div>
          <div>
            <p className="text-muted-foreground">Location</p>
            <p className="font-medium">{state.basics.location || 'Not set'}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Host state</p>
            <p className="font-medium">{state.basics.hostState ?? 'Not set'}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Organization & Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div>
            <p className="text-muted-foreground">Organization</p>
            <p className="font-medium">{selectedOrg?.name ?? 'Not set'}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {OPTION_CONFIG.map(option => {
              const enabled = state.options[option.key]
              const labelText =
                option.key === 'labelPrintingDisabled'
                  ? enabled
                    ? 'Label printing disabled'
                    : 'Label printing enabled'
                  : enabled
                    ? option.label
                    : `No ${option.label.toLowerCase()}`
              return (
                <Badge key={option.key} variant={enabled ? 'default' : 'outline'}>
                  {labelText}
                </Badge>
              )
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Classes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {state.classes.length === 0 ? (
            <p className="text-muted-foreground">No classes selected. You can add them later.</p>
          ) : (
            state.classes.map(selection => {
              const autoPlan = autoPlans.get(selection.id)
              const manualLabel = [selection.divisionCode, selection.divisionName]
                .filter(Boolean)
                .join(' • ')
              return (
                <div key={selection.id} className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium">{selection.name || selection.catalogClassName}</p>
                    <p className="text-xs text-muted-foreground">
                      {selection.code || selection.catalogCategoryCode || 'Catalog selection'}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground text-right">
                    {manualLabel
                      ? `Division: ${manualLabel}`
                      : autoPlan
                        ? `Default division: ${autoPlan.divisionName} (${autoPlan.divisionCode})`
                        : 'Division will be created automatically'}
                  </p>
                </div>
              )
            })
          )}
        </CardContent>
      </Card>

      {classFailures.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Class import issues</CardTitle>
            <CardDescription>
              These classes need attention. Fix them in Setup → Classes after leaving the wizard.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {classFailures.map(failure => (
              <div
                key={failure.id}
                className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900"
              >
                <p className="font-medium">{failure.name}</p>
                <p>{failure.message}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Staff</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {VISIBLE_STAFF_ROLES.map(role => (
            <div key={role} className="flex items-center justify-between">
              <span>{STAFF_ROLE_CONFIG[role].title}</span>
              <span className="font-medium">{staffValidation.counts[role]}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {failedStaff.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Staff errors</CardTitle>
            <CardDescription>We could not add the following staff members.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {failedStaff.map(failed => (
              <div
                key={failed.id}
                className="rounded-md border border-destructive/50 bg-destructive/5 p-3"
              >
                <p className="font-medium">{failed.name ?? failed.personId ?? 'Unknown staff'}</p>
                <p className="text-xs text-muted-foreground">
                  Role: {STAFF_ROLE_CONFIG[failed.role].title}
                </p>
                <p className="text-sm text-destructive">{failed.error}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

type StepFooterProps = {
  disableBack?: boolean
  onBack?: () => void
  onNext?: () => void
  nextDisabled?: boolean
  nextLabel?: string
  extraAction?: ReactNode
}

function StepFooter({
  disableBack,
  onBack,
  onNext,
  nextDisabled,
  nextLabel = 'Next',
  extraAction,
}: StepFooterProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <Button type="button" variant="outline" onClick={onBack} disabled={disableBack || !onBack}>
        Back
      </Button>
      <div className="flex items-center gap-2">
        {extraAction}
        <Button type="button" onClick={onNext} disabled={nextDisabled}>
          {nextLabel}
        </Button>
      </div>
    </div>
  )
}
